import React, { useState } from 'react';
import {
  X,
  Truck,
  Calendar,
  Clock,
  MapPin,
  User,
  Users,
  FileCheck,
  Printer,
  ShieldCheck,
  AlertCircle,
  PenTool
} from 'lucide-react';
import { OrdemDeServico } from '../../types/ordemServico';
import { AssinaturaDigitalPad } from '../../components/areaCliente/AssinaturaDigitalPad';
import { areaClienteService } from '../../services/areaClienteService';

interface Props {
  os: OrdemDeServico | null;
  onClose: () => void;
  onAssinado?: () => void;
}

export function AreaClienteDetalhesOSModal({ os, onClose, onAssinado }: Props) {
  const [assinaturaBase64, setAssinaturaBase64] = useState<string | null>(null);
  const [nomeSignatario, setNomeSignatario] = useState('');
  const [documentoSignatario, setDocumentoSignatario] = useState('');
  const [enviandoAssinatura, setEnviandoAssinatura] = useState(false);
  const [erroAssinatura, setErroAssinatura] = useState<string | null>(null);
  const [assinadoAgora, setAssinadoAgora] = useState(false);

  if (!os) return null;

  const getStatusInfo = (statusId?: number) => {
    switch (statusId) {
      case 8:
        return { label: 'Solicitado / Em Aberto', color: 'bg-amber-100 text-amber-900 border-amber-300' };
      case 1:
        return { label: 'Agendado', color: 'bg-blue-100 text-blue-900 border-blue-300' };
      case 2:
        return { label: 'Em Andamento', color: 'bg-orange-100 text-orange-900 border-orange-300' };
      case 4:
        return { label: 'Finalizado', color: 'bg-emerald-100 text-emerald-900 border-emerald-300' };
      case 7:
        return { label: 'Medição Gerada', color: 'bg-green-100 text-green-900 border-green-300' };
      case 9:
        return { label: 'Faturado', color: 'bg-purple-100 text-purple-900 border-purple-300' };
      case 3:
        return { label: 'Cancelado', color: 'bg-red-100 text-red-900 border-red-300' };
      case 6:
        return { label: 'Aguardando Assinatura', color: 'bg-yellow-100 text-yellow-900 border-yellow-300' };
      default:
        return { label: `Status #${statusId}`, color: 'bg-slate-100 text-slate-800 border-slate-300' };
    }
  };

  const statusInfo = getStatusInfo(os.status_id);

  const handlePrint = () => {
    window.print();
  };

  const handleConfirmarAssinatura = async () => {
    setErroAssinatura(null);
    if (!assinaturaBase64) {
      setErroAssinatura('Assine no quadro acima antes de confirmar.');
      return;
    }
    if (!nomeSignatario.trim()) {
      setErroAssinatura('Informe o nome de quem está assinando.');
      return;
    }
    setEnviandoAssinatura(true);
    try {
      const ok = await areaClienteService.assinarOrdemServico(os.id, {
        assinatura: assinaturaBase64,
        nomeSignatario: nomeSignatario.trim(),
        documentoSignatario: documentoSignatario.trim(),
      });
      if (ok) {
        setAssinadoAgora(true);
        onAssinado?.();
      } else {
        setErroAssinatura('Não foi possível registrar a assinatura. Tente novamente.');
      }
    } catch (err: any) {
      setErroAssinatura(err.message || 'Erro ao registrar assinatura.');
    } finally {
      setEnviandoAssinatura(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden border border-slate-200 animate-in fade-in zoom-in-95 duration-150">
        
        {/* Modal Header */}
        <div className="bg-slate-900 text-white p-5 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-red-600/30 border border-red-500/40 rounded-xl text-red-400">
              <Truck className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-extrabold tracking-tight">Ordem de Serviço #{os.id}</h3>
                <span className={`px-2.5 py-0.5 text-xs font-bold rounded-full border ${statusInfo.color}`}>
                  {statusInfo.label}
                </span>
              </div>
              <p className="text-xs text-slate-400">
                Data: {os.data_atendimento ? new Date(os.data_atendimento + 'T00:00:00').toLocaleDateString('pt-BR') : 'A definir'}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={handlePrint}
              className="p-2 text-slate-300 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
              title="Imprimir comprovante"
            >
              <Printer className="w-4 h-4" />
            </button>
            <button
              onClick={onClose}
              className="p-2 text-slate-300 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div className="p-6 space-y-6 max-h-[75vh] overflow-y-auto">
          
          {/* Status Alert for "Solicitado" */}
          {os.status_id === 8 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3.5 flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="text-xs text-amber-900">
                <p className="font-bold">Frete Solicitado em Aberto</p>
                <p className="text-amber-800 mt-0.5">
                  Sua solicitação foi registrada no sistema e está aguardando confirmação e alocação de motorista pela nossa equipe operacional.
                </p>
              </div>
            </div>
          )}

          {/* Description / Carga */}
          <div className="space-y-1">
            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Descrição dos Serviços</span>
            <p className="text-sm font-semibold text-slate-800 bg-slate-50 p-3 rounded-xl border border-slate-100">
              {os.descricao || 'Transporte e movimentação de cargas'}
            </p>
          </div>

          {/* Addresses / Rota */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 rounded-xl border border-slate-200 bg-slate-50/50 space-y-1.5">
              <div className="flex items-center space-x-1.5 text-xs font-bold text-slate-500 uppercase">
                <MapPin className="w-3.5 h-3.5 text-red-600" />
                <span>Local de Retirada (Origem)</span>
              </div>
              <p className="text-xs font-medium text-slate-800 leading-relaxed">
                {os.retirada || 'Não especificado'}
              </p>
            </div>

            <div className="p-4 rounded-xl border border-slate-200 bg-slate-50/50 space-y-1.5">
              <div className="flex items-center space-x-1.5 text-xs font-bold text-slate-500 uppercase">
                <MapPin className="w-3.5 h-3.5 text-emerald-600" />
                <span>Local de Entrega (Destino)</span>
              </div>
              <p className="text-xs font-medium text-slate-800 leading-relaxed">
                {os.entrega || 'Não especificado'}
              </p>
            </div>
          </div>

          {/* Schedule & Operational Details */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="p-3 rounded-xl border border-slate-100 bg-slate-50">
              <span className="text-[10px] font-bold text-slate-400 uppercase">Data</span>
              <p className="text-xs font-bold text-slate-800 mt-1">
                {os.data_atendimento ? new Date(os.data_atendimento + 'T00:00:00').toLocaleDateString('pt-BR') : '-'}
              </p>
            </div>

            <div className="p-3 rounded-xl border border-slate-100 bg-slate-50">
              <span className="text-[10px] font-bold text-slate-400 uppercase">Horário Início</span>
              <p className="text-xs font-bold text-slate-800 mt-1">{os.horario_inicio || '-'}</p>
            </div>

            <div className="p-3 rounded-xl border border-slate-100 bg-slate-50">
              <span className="text-[10px] font-bold text-slate-400 uppercase">Horário Final</span>
              <p className="text-xs font-bold text-slate-800 mt-1">{os.horario_final || '-'}</p>
            </div>

            <div className="p-3 rounded-xl border border-slate-100 bg-slate-50">
              <span className="text-[10px] font-bold text-slate-400 uppercase">Ajudantes</span>
              <p className="text-xs font-bold text-slate-800 mt-1">{os.quantidade_ajudante || 0} ajudante(s)</p>
            </div>
          </div>

          {/* Vehicle / Driver Info */}
          {(os.veiculos_id || os.agregado) && (
            <div className="p-4 rounded-xl border border-slate-200 bg-slate-50/50 space-y-2">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Veículo & Motorista</span>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
                <div>
                  <span className="text-slate-400">Identificação Veículo:</span>
                  <p className="font-bold text-slate-800">{os.veiculos_id || 'Alocado pela Voope'}</p>
                </div>
                <div>
                  <span className="text-slate-400">Motorista / Responsável:</span>
                  <p className="font-bold text-slate-800">{os.agregado ? `Equipe ${os.agregado}` : 'Equipe Voope'}</p>
                </div>
              </div>
            </div>
          )}

          {/* Observations */}
          {os.ocorrencias && (
            <div className="space-y-1">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Observações / Instruções</span>
              <p className="text-xs text-slate-700 bg-slate-50 p-3 rounded-xl border border-slate-100">
                {os.ocorrencias}
              </p>
            </div>
          )}

          {/* Digital Signature Confirmation */}
          {os.assinatura && (
            <div className="p-4 rounded-2xl border border-emerald-200 bg-emerald-50/50 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <FileCheck className="w-5 h-5 text-emerald-600" />
                  <div>
                    <p className="text-xs font-bold text-emerald-900">Comprovante com Assinatura Digital</p>
                    <p className="text-[10px] text-emerald-700">
                      {os.rg_assinatura ? `Documento Signatário: ${os.rg_assinatura}` : 'Assinado eletronicamente'}
                    </p>
                  </div>
                </div>
                <span className="px-2 py-1 bg-emerald-100 text-emerald-800 text-[10px] font-bold rounded-md">
                  Validado
                </span>
              </div>
              <div className="p-2 bg-white rounded-xl border border-emerald-200 inline-block">
                <img src={os.assinatura} alt="Assinatura Digital" className="max-h-14 object-contain" referrerPolicy="no-referrer" />
              </div>
            </div>
          )}

          {/* Assinatura remota pelo cliente: só aparece quando a OS está
              "Aguardando Assinatura" e ainda não foi assinada. */}
          {!os.assinatura && os.status_id === 6 && (
            assinadoAgora ? (
              <div className="p-4 rounded-2xl border border-emerald-200 bg-emerald-50/50 flex items-center gap-3">
                <FileCheck className="w-6 h-6 text-emerald-600 shrink-0" />
                <div>
                  <p className="text-sm font-bold text-emerald-900">Assinatura registrada com sucesso!</p>
                  <p className="text-xs text-emerald-700 mt-0.5">A Ordem de Serviço foi finalizada. Obrigado.</p>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3.5 flex items-start space-x-3">
                  <PenTool className="w-5 h-5 text-yellow-700 shrink-0 mt-0.5" />
                  <div className="text-xs text-yellow-900">
                    <p className="font-bold">Esta Ordem de Serviço está aguardando sua assinatura</p>
                    <p className="text-yellow-800 mt-0.5">Confira os dados acima e assine abaixo para confirmar a conclusão do serviço.</p>
                  </div>
                </div>

                <AssinaturaDigitalPad
                  label="Assinatura de Confirmação do Serviço"
                  required
                  onSignatureChange={(base64, nome, doc) => {
                    setAssinaturaBase64(base64);
                    setNomeSignatario(nome);
                    setDocumentoSignatario(doc);
                  }}
                />

                {erroAssinatura && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700 font-semibold">
                    {erroAssinatura}
                  </div>
                )}

                <button
                  onClick={handleConfirmarAssinatura}
                  disabled={enviandoAssinatura}
                  className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold rounded-xl transition-colors disabled:opacity-50 cursor-pointer"
                >
                  {enviandoAssinatura ? 'Enviando assinatura...' : 'Confirmar Assinatura'}
                </button>
              </div>
            )
          )}
        </div>

        {/* Modal Footer */}
        <div className="bg-slate-50 border-t border-slate-200 p-4 flex items-center justify-between">
          <span className="text-[11px] text-slate-400">Voope Transportes • Central de Rastreamento</span>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white text-xs font-bold rounded-xl transition-colors"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}
